/** Bounded, player-private state for the first authored story slice. */
export const STORY_STATE_VERSION = 1 as const;
export const STORY_STATE_MAX_FLAGS = 32;
export const STORY_STATE_MAX_NPCS = 16;
export const STORY_STATE_MAX_BYTES = 4096;

export const STORY_CHAPTERS = ["arrival"] as const;
export type StoryChapter = (typeof STORY_CHAPTERS)[number];

/** Append-only allowlist. These are consequences, not arbitrary facts. */
export const STORY_FLAGS = [
  "arrival.keeper-met",
  "arrival.operative-met",
  "arrival.site-found",
  "arrival.objective-complete",
  "arrival.reward-claimed",
] as const;
export type StoryFlag = (typeof STORY_FLAGS)[number];

export interface StoryNpcStateV1 {
  met: boolean;
  lastEvent: string;
}

export interface StoryStateV1 {
  v: typeof STORY_STATE_VERSION;
  chapter: StoryChapter;
  flags: StoryFlag[];
  /** Canonical definition slug → bounded remembered state. */
  npc: Record<string, StoryNpcStateV1>;
}

export function defaultStoryState(): StoryStateV1 {
  return { v: STORY_STATE_VERSION, chapter: "arrival", flags: [], npc: {} };
}

export function storyHas(state: StoryStateV1, flag: StoryFlag): boolean {
  return state.flags.includes(flag);
}

/** Authored chapter lines selected solely from authoritative consequence state. */
export function canonicalArrivalLine(slug: string, state: StoryStateV1): string | null {
  if (slug === "mara-venn") {
    if (storyHas(state, "arrival.reward-claimed"))
      return "The approaches are clear, and the capital knows your name. Take the win.";
    if (storyHas(state, "arrival.objective-complete"))
      return "Sable sent the reading ahead. Clean work. Let's close the ledger.";
    if (storyHas(state, "arrival.operative-met"))
      return "Sable has the field work. Bring her three clean Ferrite samples.";
    if (storyHas(state, "arrival.keeper-met"))
      return "Find Sable Rook at the field post. She'll show you what this place needs.";
    return "You found the capital. Good. Now let's give you a reason to belong to it.";
  }
  if (slug === "sable-rook") {
    if (storyHas(state, "arrival.reward-claimed"))
      return "First circuit complete. Next time, we'll range beyond the lights.";
    if (storyHas(state, "arrival.objective-complete"))
      return "The samples read clean. Mara has your reward—and a better opinion of you.";
    if (storyHas(state, "arrival.operative-met"))
      return "Three Ferrite samples. Fresh cuts, not market scrap. Bring them back here.";
    return "Mara sent you? Then you're either useful or about to become useful.";
  }
  return null;
}
